/**
 * Discussion feature that appears below the content on content pages.
 */
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {Provider} from "#data-access/react-redux-provider.tsx";

import type {DiscussionsServerResponse} from "../types.ts";
import useShowDiscussion from "../util/use-show-discussion.ts";

import {createDiscussionStore} from "./discussion-store.ts";
import Discussion from "./discussion.tsx";
import ErrorBoundary from "./error-boundary.tsx";

type DiscussionProps = JSX.LibraryManagedAttributes<
    typeof Discussion,
    React.ComponentProps<typeof Discussion>
>;

type Props = {
    cachedPosts?: DiscussionsServerResponse;
} & DiscussionProps;
type State = {
    store: any;
};

// note(matthewc) I tried to convert this to a functional component,
// but parent components need a reference to it to scroll it into view.
// I'm sure it can be done, just didn't feel like going down the rabbit hole.
class StatefulDiscussion extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            store: createDiscussionStore(
                this.props.cachedPosts,
                this.props.discussionContext,
            ),
        };
    }

    render(): React.ReactNode {
        const {cachedPosts: _, ...rest} = this.props;
        return (
            <Provider store={this.state.store}>
                <ErrorBoundary style={styles.errorBoundary}>
                    <Discussion {...rest} />
                </ErrorBoundary>
            </Provider>
        );
    }
}

const styles = StyleSheet.create({
    errorBoundary: {
        maxWidth: 688,
        margin: "auto",
    },
});

export type StatefulDiscussionType = StatefulDiscussion;

export default React.forwardRef<StatefulDiscussion, Props>((props, ref) => {
    const shouldShowDiscussion = useShowDiscussion();

    if (!shouldShowDiscussion) {
        return null;
    }

    return <StatefulDiscussion ref={ref} {...props} />;
});
